/**
 * /auth/verify — landing page after the Supabase email-verification
 * link. Supabase processes the token from the URL via
 * `detectSessionInUrl`; this page just shows a confirmation and
 * routes the user onward.
 *
 * If the user's session is already active when the verification
 * link is clicked, they're sent to / (the home shell). Otherwise
 * they're sent to /login with a banner explaining the email is
 * verified and they can sign in.
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/authStore';

export function AuthVerifyPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();

  useEffect(() => {
    // Brief delay so Supabase's `detectSessionInUrl` has time to
    // commit before we read the store. 600ms is generous; the
    // typical commit happens in < 50ms.
    const timer = setTimeout(() => {
      const user = useAuthStore.getState().user;
      if (user) {
        navigate('/', { replace: true });
      } else {
        navigate('/login?verified=1', { replace: true });
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="space-y-2 text-center">
      <h2 className="text-lg font-semibold tracking-tight">
        {t('verify.heading', { defaultValue: 'Email verified' })}
      </h2>
      <p className="text-sm text-ink-muted">
        {t('verify.body', {
          defaultValue: 'Thanks — your email is confirmed. Redirecting…',
        })}
      </p>
      <Link to="/" className="text-sm font-medium text-spark hover:underline">
        {t('verify.continue', { defaultValue: 'Continue' })}
      </Link>
    </div>
  );
}
